/**
 * Streaming detector for five common Android runtime catastrophe classes:
 *
 *   - `java_crash`     — `FATAL EXCEPTION` block (AndroidRuntime tag)
 *   - `anr`            — Application Not Responding (am_anr / ANR in <pkg>)
 *   - `native_crash`   — debuggerd / tombstone signal block
 *   - `lmk`            — low-memory killer (lmkd / am_kill)
 *   - `oom`            — Java OutOfMemoryError or kernel "Out of memory: Killed"
 *
 * The detector is deliberately stateful: each call takes a "tail window" of
 * the most recent N raw lines plus the new chunk, and returns a flat list of
 * fully-resolved events (i.e. the closing line of every block is already
 * past). The caller dedupes & pushes them into the event store.
 *
 * Why a tail window instead of streaming line-by-line? Java crash blocks
 * span ~30-50 lines but can cross WS chunk boundaries; we need enough
 * context to (a) find the `Process:` line that names the package and (b)
 * detect the end of the block when the next non-`at`-prefixed line appears.
 *
 * Performance: rules are cheap regexes evaluated only on chunk arrival,
 * not on every render. Worst-case is bounded by the tail window size
 * (typically 200 lines).
 */
import type { LogLine } from './parser';

export type LogEventKind = 'java_crash' | 'anr' | 'native_crash' | 'lmk' | 'oom';

export interface LogEvent {
  /** Stable hash (kind + fingerprint), used as React key. */
  id: string;
  kind: LogEventKind;
  /** First-line ts, formatted as parser-provided "MM-DD HH:MM:SS.mmm". */
  ts: string;
  /** Index into the *raw* logcat buffer that this event started at. */
  startIdx: number;
  /** Inclusive end index of the captured snippet. */
  endIdx: number;
  /** Package or process name when we can pluck it out of the block. */
  processName?: string;
  pid?: string;
  /** Java exception type, e.g. `java.lang.NullPointerException`. */
  exceptionType?: string;
  /** First `at com.x.y(File.java:NN)` frame. */
  firstFrame?: string;
  /** Native signal line, e.g. `signal 11 (SIGSEGV), code 1`. */
  signal?: string;
  /** One-line human readable summary (used in the drawer header). */
  summary: string;
  /** Full block text (≤ ~200 lines), for copy and saving. */
  snippet: string;
  /** Fingerprint used by the store to fold repeats (×N). */
  fingerprint: string;
}

/* -------------------------------------------------------------------- */
/* Regex helpers                                                        */
/* -------------------------------------------------------------------- */

const RE_FATAL = /FATAL EXCEPTION:/;
const RE_PROCESS = /Process:\s*([^,]+),\s*PID:\s*(\d+)/;
const RE_AT_FRAME = /^\s+at\s+([\w.$<>]+(?:\([^)]*\))?)/;
const RE_JAVA_EX_TYPE = /^([A-Za-z_][\w.$]*Exception|[A-Za-z_][\w.$]*Error)(?::|$)/;
const RE_CAUSED_BY = /^Caused by:/;
const RE_MORE = /^\s*\.\.\.\s*\d+\s+more$/;

const RE_ANR_HEADER = /(?:^|\s)ANR in\s+([\w.$]+)/;
const RE_ANR_REASON = /^Reason:\s*(.+)$/;

const RE_NATIVE_HEADER = /^\*{3}\s*\*{3}\s*\*{3}/;
const RE_NATIVE_PID = /^pid:\s*(\d+),\s*tid:\s*\d+,\s*name:\s*(\S+)/i;
const RE_NATIVE_SIGNAL = /^(signal\s+\d+\s*\([^)]+\),?\s*code\s*\d+.*)$/i;
const RE_NATIVE_PROCESS = /^Cmdline:\s*(\S+)/;

const RE_LMK = /am_kill:|Killing\s+\S.*adj/;
const RE_LMK_ADJ = /Killing\s+'?(\S+?)'?\s.*?adj[=\s]+(-?\d+)/;

const RE_OOM_JAVA = /java\.lang\.OutOfMemoryError/;
const RE_OOM_KERNEL = /Out of memory:\s+Killed process\s+(\d+)\s*\(([^)]+)\)/;

/* -------------------------------------------------------------------- */
/* Public API                                                           */
/* -------------------------------------------------------------------- */

export interface DetectCtx {
  /**
   * Absolute index (in the raw ring) of `lines[0]`. The detector uses this
   * to compute `LogEvent.startIdx` / `endIdx` such that the caller can map
   * back to the displayed list without having to slice again.
   */
  baseIdx: number;
  /** Lines to scan. Older context first, newest last. */
  lines: LogLine[];
  /**
   * If set, the detector skips events whose startIdx < this value. The
   * caller uses this to incrementally process only new chunks across calls
   * while still keeping a tail-window for context.
   */
  minStartIdx?: number;
}

export function detectEvents(ctx: DetectCtx): LogEvent[] {
  const out: LogEvent[] = [];
  const { lines, baseIdx } = ctx;
  const minStart = ctx.minStartIdx ?? -Infinity;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (RE_FATAL.test(l.message) || (l.tag === 'AndroidRuntime' && RE_FATAL.test(l.raw))) {
      const ev = parseJavaCrash(lines, i, baseIdx);
      if (ev && ev.startIdx >= minStart) out.push(ev);
    } else if (
      RE_ANR_HEADER.test(l.raw) ||
      (l.tag === 'ActivityManager' && /am_anr:/.test(l.message))
    ) {
      const ev = parseAnr(lines, i, baseIdx);
      if (ev && ev.startIdx >= minStart) out.push(ev);
    } else if (RE_NATIVE_HEADER.test(l.message)) {
      const ev = parseNativeCrash(lines, i, baseIdx);
      if (ev && ev.startIdx >= minStart) out.push(ev);
    } else if (RE_LMK.test(l.raw)) {
      const ev = parseLmk(l, baseIdx + i);
      if (ev && ev.startIdx >= minStart) out.push(ev);
    } else if (RE_OOM_JAVA.test(l.message)) {
      const ev = parseJavaOom(lines, i, baseIdx);
      if (ev && ev.startIdx >= minStart) out.push(ev);
    } else if (RE_OOM_KERNEL.test(l.raw)) {
      const ev = parseKernelOom(l, baseIdx + i);
      if (ev && ev.startIdx >= minStart) out.push(ev);
    }
  }
  // Dedupe events that share the same fingerprint+startIdx (e.g. when the
  // same chunk includes both "FATAL EXCEPTION" and "Process:" twice during
  // the same crash for some odd device).
  const seen = new Set<string>();
  return out.filter((e) => {
    const k = `${e.startIdx}:${e.fingerprint}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/* -------------------------------------------------------------------- */
/* Per-kind extractors                                                  */
/* -------------------------------------------------------------------- */

function parseJavaCrash(lines: LogLine[], i: number, baseIdx: number): LogEvent | null {
  // Walk backward up to 5 lines in case the FATAL line is *after* the
  // "Process:" header (rarely happens but defensive).
  let processName: string | undefined;
  let pid: string | undefined;
  for (let k = Math.max(0, i - 5); k < lines.length && k < i + 25; k++) {
    const m = RE_PROCESS.exec(lines[k].raw);
    if (m) {
      processName = m[1].trim();
      pid = m[2];
      break;
    }
  }
  // Walk forward to find:
  //   - exception type (the first non-`at`/`Caused by`/`... more` line that's
  //     not the FATAL header line itself)
  //   - first `at` frame
  //   - end-of-block (a line that doesn't look like a stack frame and is
  //     past at least one stack frame)
  let exceptionType: string | undefined;
  let firstFrame: string | undefined;
  let end = i;
  let sawFrame = false;
  for (let k = i + 1; k < Math.min(lines.length, i + 200); k++) {
    const msg = lines[k].message;
    const atM = RE_AT_FRAME.exec(msg);
    if (atM) {
      sawFrame = true;
      if (!firstFrame) firstFrame = atM[1];
      end = k;
      continue;
    }
    if (RE_CAUSED_BY.test(msg) || RE_MORE.test(msg)) {
      end = k;
      continue;
    }
    if (!exceptionType) {
      const exM = RE_JAVA_EX_TYPE.exec(msg);
      if (exM) {
        exceptionType = exM[1];
        end = k;
        continue;
      }
    }
    if (sawFrame) break;
    end = k;
  }
  const startIdx = baseIdx + i;
  const endIdx = baseIdx + end;
  const snippet = lines
    .slice(i, end + 1)
    .map((l) => l.raw)
    .join('\n');
  const fingerprint = `java_crash|${processName ?? '?'}|${exceptionType ?? '?'}|${firstFrame ?? '?'}`;
  const summary = `J ${shortTs(lines[i].ts)} · ${processName ?? `pid ${lines[i].pid}`} · ${exceptionType ?? 'FATAL'}${firstFrame ? ' · ' + shortFrame(firstFrame) : ''}`;
  return {
    id: hash(`${startIdx}-${fingerprint}`),
    kind: 'java_crash',
    ts: lines[i].ts,
    startIdx,
    endIdx,
    processName,
    pid,
    exceptionType,
    firstFrame,
    summary,
    snippet,
    fingerprint,
  };
}

function parseAnr(lines: LogLine[], i: number, baseIdx: number): LogEvent | null {
  const head = lines[i];
  const anrM = RE_ANR_HEADER.exec(head.raw);
  const processName = anrM?.[1];
  let reason: string | undefined;
  let end = i;
  for (let k = i + 1; k < Math.min(lines.length, i + 30); k++) {
    const m = RE_ANR_REASON.exec(lines[k].message);
    if (m) {
      reason = m[1].trim();
      end = k;
      break;
    }
    end = k;
  }
  // Capture some context after the Reason for the snippet.
  end = Math.min(lines.length - 1, end + 10);
  const startIdx = baseIdx + i;
  const endIdx = baseIdx + end;
  const snippet = lines
    .slice(i, end + 1)
    .map((l) => l.raw)
    .join('\n');
  const fingerprint = `anr|${processName ?? '?'}|${reason ?? '?'}`;
  const summary = `A ${shortTs(head.ts)} · ${processName ?? `pid ${head.pid}`} · ANR${reason ? ' · ' + reason : ''}`;
  return {
    id: hash(`${startIdx}-${fingerprint}`),
    kind: 'anr',
    ts: head.ts,
    startIdx,
    endIdx,
    processName,
    pid: head.pid || undefined,
    summary,
    snippet,
    fingerprint,
  };
}

function parseNativeCrash(lines: LogLine[], i: number, baseIdx: number): LogEvent | null {
  let pid: string | undefined;
  let processName: string | undefined;
  let signal: string | undefined;
  let firstFrame: string | undefined;
  let end = i;
  for (let k = i + 1; k < Math.min(lines.length, i + 80); k++) {
    const msg = lines[k].message;
    if (!processName) {
      const cm = RE_NATIVE_PROCESS.exec(msg);
      if (cm) processName = cm[1];
    }
    if (!pid) {
      const pm = RE_NATIVE_PID.exec(msg);
      if (pm) {
        pid = pm[1];
        if (!processName) processName = pm[2];
      }
    }
    if (!signal) {
      const sm = RE_NATIVE_SIGNAL.exec(msg);
      if (sm) signal = sm[1];
    }
    if (!firstFrame) {
      const fm = /^\s*#\d{2}\s+pc\s+[0-9a-f]+\s+([^\s]+)/i.exec(msg);
      if (fm) firstFrame = fm[1];
    }
    // Terminate at next *** *** header (next tombstone) or after 80 lines.
    if (k > i && RE_NATIVE_HEADER.test(msg)) {
      end = k - 1;
      break;
    }
    end = k;
  }
  const startIdx = baseIdx + i;
  const endIdx = baseIdx + end;
  const snippet = lines
    .slice(i, end + 1)
    .map((l) => l.raw)
    .join('\n');
  const fingerprint = `native_crash|${processName ?? '?'}|${signal ?? '?'}|${firstFrame ?? '?'}`;
  const summary = `N ${shortTs(lines[i].ts)} · ${processName ?? `pid ${pid ?? lines[i].pid}`} · ${signal ?? 'signal ?'}${firstFrame ? ' · ' + shortFrame(firstFrame) : ''}`;
  return {
    id: hash(`${startIdx}-${fingerprint}`),
    kind: 'native_crash',
    ts: lines[i].ts,
    startIdx,
    endIdx,
    processName,
    pid,
    signal,
    firstFrame,
    summary,
    snippet,
    fingerprint,
  };
}

function parseLmk(l: LogLine, absIdx: number): LogEvent | null {
  const m = RE_LMK_ADJ.exec(l.raw);
  const processName = m?.[1];
  const adj = m?.[2];
  const fingerprint = `lmk|${processName ?? '?'}|${adj ?? '?'}`;
  const summary = `K ${shortTs(l.ts)} · killed ${processName ?? '(unknown)'}${adj ? ' · adj ' + adj : ''}`;
  return {
    id: hash(`${absIdx}-${fingerprint}`),
    kind: 'lmk',
    ts: l.ts,
    startIdx: absIdx,
    endIdx: absIdx,
    processName,
    pid: l.pid || undefined,
    summary,
    snippet: l.raw,
    fingerprint,
  };
}

function parseJavaOom(lines: LogLine[], i: number, baseIdx: number): LogEvent | null {
  // Treat the OOM as a Java crash variant; reuse process detection.
  let processName: string | undefined;
  for (let k = Math.max(0, i - 5); k < Math.min(lines.length, i + 5); k++) {
    const pm = RE_PROCESS.exec(lines[k].raw);
    if (pm) {
      processName = pm[1].trim();
      break;
    }
  }
  const end = Math.min(lines.length - 1, i + 5);
  const snippet = lines
    .slice(i, end + 1)
    .map((l) => l.raw)
    .join('\n');
  const fingerprint = `oom|${processName ?? '?'}|java`;
  const summary = `O ${shortTs(lines[i].ts)} · ${processName ?? `pid ${lines[i].pid}`} · OutOfMemoryError`;
  return {
    id: hash(`${baseIdx + i}-${fingerprint}`),
    kind: 'oom',
    ts: lines[i].ts,
    startIdx: baseIdx + i,
    endIdx: baseIdx + end,
    processName,
    pid: lines[i].pid || undefined,
    exceptionType: 'java.lang.OutOfMemoryError',
    summary,
    snippet,
    fingerprint,
  };
}

function parseKernelOom(l: LogLine, absIdx: number): LogEvent | null {
  const m = RE_OOM_KERNEL.exec(l.raw);
  const pid = m?.[1];
  const processName = m?.[2];
  const fingerprint = `oom|${processName ?? '?'}|kernel`;
  const summary = `O ${shortTs(l.ts)} · ${processName ?? '(unknown)'} · kernel OOM kill${pid ? ' · pid ' + pid : ''}`;
  return {
    id: hash(`${absIdx}-${fingerprint}`),
    kind: 'oom',
    ts: l.ts,
    startIdx: absIdx,
    endIdx: absIdx,
    processName,
    pid,
    summary,
    snippet: l.raw,
    fingerprint,
  };
}

/* -------------------------------------------------------------------- */
/* Tiny utils                                                           */
/* -------------------------------------------------------------------- */

function shortTs(ts: string): string {
  // "11-23 17:30:00.123" → "17:30:00"
  const m = /\s(\d{2}:\d{2}:\d{2})/.exec(ts);
  return m ? m[1] : ts;
}

function shortFrame(frame: string): string {
  if (frame.length <= 60) return frame;
  return '…' + frame.slice(-58);
}

/**
 * Compact 32-bit FNV-1a hash → 8-char hex. Just unique enough for React
 * keys; collision risk is irrelevant since we also key by startIdx.
 */
function hash(s: string): string {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}
